import { Dispatch, DispatchWithoutAction, FC, HTMLAttributes, useContext, useState } from 'react'
import classNames from 'classnames'
import './ChannelsSelectionHandle.scss'
import { Position } from '../../types'
import { useVisiblePixelSize } from '../hooks/useVisiblePixelSize'
import { InterfaceEvent } from '../../interfaces/InterfaceEvent'
import { useDrag } from '../../hooks/useDrag'
import { useDisableUserSelect } from '../../hooks/useDisableUserSelect'
import { useDocumentCursor } from '../../hooks/useDocumentCursor'
import clamp from 'lodash/clamp'
import { InternalContext } from '../contexts/InternalContext'

interface Props extends HTMLAttributes<HTMLButtonElement> {
  position: Position
  min: Position
  max: Position
  setPosition: Dispatch<Position>
  updateSelection: DispatchWithoutAction
}

export const ChannelsSelectionHandle: FC<Props> = ({
  position,
  min,
  max,
  setPosition,
  updateSelection,
  className,
  children,
  ...attrs
}) => {
  const { setMouseCursorEnabled } = useContext(InternalContext)
  const [element, setElement] = useState<HTMLButtonElement | null>(null)
  const [initialState, setInitialState] = useState<{ position: Position; clientX: number } | null>(null)
  const pixelSize = useVisiblePixelSize()

  const onMove = ({ clientX }: InterfaceEvent) => {
    if (initialState) {
      const delta = Math.round((clientX - initialState.clientX) * pixelSize)

      setPosition(clamp(initialState.position + delta, min, max))
    }
  }

  const drag = useDrag(element, {
    onDown({ clientX }: InterfaceEvent) {
      setInitialState({ position, clientX })
      setMouseCursorEnabled(false)
    },
    onMove,
    onUp() {
      initialState?.position !== position && updateSelection()
      setMouseCursorEnabled(true)
    },
  })

  useDisableUserSelect(drag)
  useDocumentCursor('ew-resize', drag)

  return (
    <button className={classNames('ChannelsSelectionHandle', className)} {...attrs} ref={setElement}>
      {children}
    </button>
  )
}
